import { create } from 'zustand';

/**
 * In-memory auth state. Access token NEVER goes to localStorage/sessionStorage
 * (per CLAUDE.md). Refresh token lives in an httpOnly cookie the server sets
 * on login/refresh, never visible to JS.
 */
export type AuthUser = {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  status: string;
  kycStatus: string;
  roles: string[];
  perms: string[];
  totpEnabled: boolean;
  // QoL Slice 2 — when true, the SPA forces the user through a change-password
  // screen before any other route resolves. Cleared by the next /me/password.
  mustChangePassword?: boolean;
  referralCode: string | null;
  /** POS pin — a single branch. */
  branchId?: string | null;
  /** Admin visibility set; empty = unconfined. Always a superset of branchId. */
  branchIds?: string[];
  tenantPolicy: {
    member2faRequired: boolean;
  };
};

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  setMustChangePassword: (value: boolean) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  setSession: (token, user) => set({ token, user }),
  setMustChangePassword: (value) =>
    set((s) => (s.user ? { user: { ...s.user, mustChangePassword: value } } : s)),
  clear: () => set({ token: null, user: null }),
}));

export function hasPerm(perm: string): boolean {
  const { user } = useAuthStore.getState();
  if (!user) return false;
  if (user.roles.includes('super_admin')) return true;
  return user.perms.includes(perm);
}

export function hasRole(role: string): boolean {
  const user = useAuthStore.getState().user;
  return user ? user.roles.includes(role) : false;
}

/**
 * Reactive `hasPerm` for components — re-renders when the auth store
 * changes. The non-React `hasPerm` above is fine for one-shot checks
 * inside event handlers but doesn't subscribe.
 */
export function useHasPerm(perm: string): boolean {
  return useAuthStore((s) => {
    if (!s.user) return false;
    if (s.user.roles.includes('super_admin')) return true;
    return s.user.perms.includes(perm);
  });
}

/** Reactive super-admin check — for platform-tier UI (e.g. Layout & Theme). */
export function useIsSuperAdmin(): boolean {
  return useAuthStore((s) => !!s.user?.roles.includes('super_admin'));
}
